<script>
  import { beforeUpdate } from 'svelte'
  import { auth } from '../../services/auth.js'
  import Alert from '../../components/Alert.svelte'
  import Submenu from '../../components/Submenu.svelte'
  import Membership from './Membership.svelte'
  import Orders from './Orders.svelte'
  import Address from './Address.svelte'
  import Settings from './Settings.svelte'
  import Favorites from './Favorites.svelte'
  import Payments from './Payments.svelte'
  import GiftCardBalance from './GiftCardBalance.svelte'
  import Stats from './Stats.svelte'

  const pages = [
    { url_path: 'membership',  name: 'Membership',  icon: 'star',          component: Membership },
    { url_path: 'stats',       name: 'Puzzle Stats', icon: 'bar-chart',    component: Stats },
    { url_path: 'gift-cards',  name: 'Gift Cards',  icon: 'gift',          component: GiftCardBalance },
    { url_path: 'orders',      name: 'Orders',      icon: 'cart',          component: Orders },
    { url_path: 'address',     name: 'Address',     icon: 'home',          component: Address },
    { url_path: 'favorites',   name: 'Favorites',   icon: 'puzzle-piece-2',component: Favorites },
    { url_path: 'payments',    name: 'Payments',    icon: 'cc-stripe',     component: Payments },
    { url_path: 'settings',    name: 'Settings',    icon: 'edit',          component: Settings },
  ]

  export var params
  var active
  var loggedInUser = auth.getAuthenticatedUser()

  function setActive() {
    active = pages.find((p) => params.id === p.url_path)
    if (!active) active = pages[0]
  }

  beforeUpdate(() => {
    setActive()
  })
</script>

<style type="text/less">
  .account-wrap {
    max-width: var(--content-width-sm);
    margin: 0 auto;
    padding: var(--spacing);
  }

  .account-layout {
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: var(--spacing);
    align-items: start;
  }

  // ── Sidebar ───────────────────────────────────────────────────────
  .account-sidebar {
    background: var(--col-gray-100);
    border: 1px solid var(--col-gray-80);
    border-radius: 8px;
    overflow: hidden;
    position: sticky;
    top: calc(var(--header-height) + var(--spacing-sm));
  }

  .account-profile {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    padding: var(--spacing-sm);
    background: var(--col-accent-2);
    color: var(--col-gray-100);
  }

  .account-avatar {
    width: 2.4rem;
    height: 2.4rem;
    background: rgba(255,255,255,0.15);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .icon {
      width: 1.2em;
      height: 1.2em;
      color: var(--col-gray-100);
    }
  }

  .account-profile-label {
    font-weight: 700;
    font-size: var(--font-size-sm);
    line-height: 1.2;
    color: var(--col-gray-100);
  }

  .account-nav {
    padding: var(--spacing-xs);
  }

  .account-sidebar-footer {
    padding: var(--spacing-xs);
    border-top: 1px solid var(--col-gray-90);
  }

  .logout-link {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    padding: var(--spacing-sm);
    text-decoration: none;
    border-radius: var(--border-radius);
    font-size: var(--font-size-sm);
    color: var(--col-danger);
    transition: background 0.1s ease;

    &:link, &:visited {
      color: var(--col-danger);
    }

    &:hover {
      background: var(--col-gray-95);
      text-decoration: none;
    }

    .icon {
      opacity: 0.7;
    }
  }

  // ── Content area ──────────────────────────────────────────────────
  .account-content {
    background: var(--col-gray-100);
    border: 1px solid var(--col-gray-80);
    border-radius: 8px;
    padding: var(--spacing);
    min-height: 400px;
  }

  // ── Not-logged-in state ───────────────────────────────────────────
  .account-unauthenticated {
    max-width: 480px;
    margin: 0 auto;
  }

  // ── Responsive ───────────────────────────────────────────────────
  @media (max-width: 700px) {
    .account-wrap {
      padding: var(--spacing-sm);
    }

    .account-layout {
      grid-template-columns: 1fr;
      gap: var(--spacing-sm);
    }

    .account-sidebar {
      position: static;
    }

    .account-content {
      padding: var(--spacing-sm);
    }
  }
</style>

<div class="account-wrap">
  {#if !loggedInUser}
    <div class="account-unauthenticated">
      <h1>My Account</h1>
      <Alert type="danger" icon="warning">
        You must
        <a href="/register">create an account</a>
        or
        <a href="/login?referrer={window.location.pathname}">sign in</a>
        to view your account.
      </Alert>
    </div>
  {:else}
    <div class="account-layout">
      <aside class="account-sidebar">
        <div class="account-profile">
          <div class="account-avatar">
            <svg class="icon"><use href="/icons.svg#person"></use></svg>
          </div>
          <div class="account-profile-label">My Account</div>
        </div>

        <div class="account-nav">
          <Submenu {pages} vertical={true} urlBase="/account/" activePath={params.id} />
        </div>

        <div class="account-sidebar-footer">
          <a href="/logout" rel="external" class="logout-link">
            <svg class="icon"><use href="/icons.svg#logout"></use></svg>
            Sign Out
          </a>
        </div>
      </aside>

      <main class="account-content">
        <svelte:component this={active.component} refer="my-account" />
      </main>
    </div>
  {/if}
</div>
